import type { SessionSummary } from "@backstory/protocol";
import { formatCityRegion } from "../lib/country";
import { formatDuration, formatRelative } from "../lib/format";
import { ErrorsBadge, GapsIcon, LevelBadge } from "./Badges";
import { EndUser, endUserInitials } from "./EndUser";
import { RowMenu } from "./ui";

/**
 * Still recording: the SDK has not ended the segment and events are still
 * arriving. Worth flagging in the list because it is the one state an agent can
 * still act on — they can join the session instead of reading it afterwards.
 */
function live(s: SessionSummary): boolean {
  if (s.endedAt) return false;
  return Date.now() - new Date(s.lastEventAt).getTime() < 60_000;
}

function ReviewBadge({ session: s }: { session: SessionSummary }) {
  if (s.reviewedAt) {
    return <span className="badge ok" title={`Reviewed ${formatRelative(s.reviewedAt)}${s.reviewedBy ? ` by ${s.reviewedBy}` : ""}`}>Reviewed</span>;
  }
  if (s.lastViewedAt) {
    return <span className="badge neutral" title={`Last viewed ${formatRelative(s.lastViewedAt)}${s.lastViewedBy ? ` by ${s.lastViewedBy}` : ""}`}>Viewed</span>;
  }
  return <span className="badge ai" title="No one has opened this replay">New</span>;
}

export interface SessionsTableProps {
  sessions: SessionSummary[];
  onOpen: (id: string) => void;
  /** Present for roles that may delete a session. The menu is omitted otherwise. */
  onDelete?: (session: SessionSummary) => void;
  loading?: boolean;
}

function SessionActions({ session, onDelete }: { session: SessionSummary; onDelete?: (session: SessionSummary) => void }) {
  if (!onDelete) return null;
  return (
    <span className="session-actions" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <RowMenu label={`Actions for session ${session.id}`}>
        <button type="button" role="menuitem" className="danger" onClick={() => onDelete(session)}>Delete session</button>
      </RowMenu>
    </span>
  );
}

export function SessionsTable({ sessions, onOpen, onDelete, loading }: SessionsTableProps) {
  const columns = onDelete ? 6 : 5;
  return (
    <div className="sessions-results" aria-busy={loading || undefined}>
      <table className="table sessions-table">
        <thead>
          <tr>
            <th>User</th>
            <th>Session</th>
            <th>When</th>
            <th>Release</th>
            <th>Status</th>
            {onDelete ? <th className="session-actions-col"><span className="sr-only">Actions</span></th> : null}
          </tr>
        </thead>
        <tbody>
          {sessions.map((s) => (
            <tr
              key={s.id}
              onClick={() => onOpen(s.id)}
              onKeyDown={(e) => (e.key === "Enter" ? onOpen(s.id) : undefined)}
              tabIndex={0}
              data-testid="session-row"
            >
              <td>
                <EndUser id={s.endUserId} name={s.endUserName} place={formatCityRegion(s)} avatar />
              </td>
              <td>
                <strong className="mono">{s.id}</strong>
              </td>
              <td>
                <span className="cell-stack">
                  <strong title={s.startedAt}>{formatRelative(s.startedAt)}</strong>
                  <small>{formatDuration(s.durationMs)}</small>
                </span>
              </td>
              <td>
                <span className="mono">{s.release ?? "—"}</span>
              </td>
              <td>
                <span className="cell-badges">
                  {live(s) && (
                    <span className="badge live" title="Still recording">
                      ● Live
                    </span>
                  )}
                  <ReviewBadge session={s} />
                  <LevelBadge level={s.level} />
                  {s.errorCount > 0 && <ErrorsBadge count={s.errorCount} />}
                  {s.hasGaps && <GapsIcon hasGaps />}
                </span>
              </td>
              {onDelete ? (
                <td>
                  <SessionActions session={s} onDelete={onDelete} />
                </td>
              ) : null}
            </tr>
          ))}
          {loading &&
            Array.from({ length: 6 }).map((_, i) => (
              <tr key={`sk-${i}`} aria-hidden="true">
                {Array.from({ length: columns }).map((__, j) => (
                  <td key={j}>
                    <span className="skeleton">loading</span>
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>

      <div className="session-card-list" aria-label="Sessions">
        {sessions.map((s) => {
          const place = formatCityRegion(s);
          return (
            <div className="session-card" key={s.id} tabIndex={0} onClick={() => onOpen(s.id)} onKeyDown={(e) => (e.key === "Enter" ? onOpen(s.id) : undefined)} data-testid="session-card">
              <span className="session-card-head">
                <span className="session-card-who">
                  <span className={`session-avatar${s.endUserId ? "" : " anon"}`} aria-hidden="true">
                    {s.endUserId ? endUserInitials(s.endUserId, s.endUserName) : "?"}
                  </span>
                  <span className="session-card-ident">
                    <strong className={s.endUserName ? undefined : "mono"} title={s.endUserName ?? s.endUserId}>
                      {s.endUserName ?? s.endUserId ?? "Anonymous"}
                    </strong>
                    {place && <span className="end-user-place" title={place}>{place}</span>}
                    {s.endUserName && s.endUserId && <span className="end-user-id mono">{s.endUserId}</span>}
                    <span className="session-card-when">
                      <span title={s.startedAt}>{formatRelative(s.startedAt)}</span>
                      <span aria-hidden="true">·</span>
                      <span title="Session duration">{formatDuration(s.durationMs)}</span>
                    </span>
                  </span>
                </span>
                {onDelete ? <SessionActions session={s} onDelete={onDelete} /> : (
                  <svg className="session-card-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="m9 6 6 6-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="session-card-session">
                <span className="session-card-title">Session</span>
                <span className="session-card-id mono">{s.id}</span>
              </span>
              <span className="session-card-badges">
                {live(s) && <span className="badge live" title="Still recording">● Live</span>}
                <ReviewBadge session={s} />
                <LevelBadge level={s.level} />
                {s.errorCount > 0 && <ErrorsBadge count={s.errorCount} />}
                {s.hasGaps && <GapsIcon hasGaps />}
              </span>
              {s.release && (
                <span className="session-card-context">
                  <span><small>Release</small><strong className="mono">{s.release}</strong></span>
                </span>
              )}
            </div>
          );
        })}
        {loading &&
          Array.from({ length: 3 }).map((_, i) => (
            <div className="session-card session-card-skeleton" key={`card-sk-${i}`} aria-hidden="true">
              <span className="skeleton">Loading session</span>
              <span className="skeleton">Loading details</span>
              <span className="skeleton">Loading stats</span>
            </div>
          ))}
      </div>
    </div>
  );
}
